// src/resources/dashboards/ProductEvolution/sections/kit.tsx
//
// Peças pequenas compartilhadas pelas 7 seções do detalhe da rede.
//
// Não é "mais um kit": o kit de verdade é ../../shared. Aqui ficam SÓ as coisas
// específicas deste painel (moeda com centavos, barra horizontal com o rótulo
// fora quando ela é estreita, legenda de recorte) que sete arquivos usariam
// igual. Duplicar isso sete vezes é exatamente o defeito que a tela antiga
// tinha — sete cópias do mesmo card com sete comportamentos diferentes.
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { COLORS, cardSx, fmtPct } from '../../shared';

/** Preço por litro em centavos importa; `fmtBrl` arredonda pra real e some com a diferença. */
export const fmtBrlPrecise = (v: number | null | undefined, decimals = 2) =>
  v === null || v === undefined || !Number.isFinite(v)
    ? '—'
    : v.toLocaleString('pt-BR', {
      style: 'currency', currency: 'BRL',
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    });

/**
 * Percentual que NÃO vira "0%" quando é pequeno.
 * Taxa de 0,4% arredondada pra inteiro some da tela e o operador conclui que
 * não há nada ali — foi defeito real do painel antigo.
 */
export const fmtPctSmall = (v: number | null | undefined, decimals = 1) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const p = v * 100;
  if (p === 0) return '0%';
  if (Math.abs(p) < 1) return `${p.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
  return fmtPct(v, decimals);
};

/** Pontos percentuais já multiplicados pelo backend — o front NÃO multiplica de novo. */
export const fmtPp = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v >= 0 ? '▲' : '▼'} ${Math.abs(v).toLocaleString('pt-BR')}%`;

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : '—';
};

export const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
};

/** "há X" em pt-BR sem puxar o plugin relativeTime global do dayjs. */
export const humanizeAgo = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = dayjs(iso);
  if (!then.isValid()) return null;
  const seconds = Math.max(0, dayjs().diff(then, 'second'));
  if (seconds < 60) return 'agora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
};

/** Rótulo do quintil de ticket. 1 = pior da carteira, 5 = melhor. */
export const quintileText = (q: number | null): string | null =>
  q === null ? null : `${q}º quintil de ticket da carteira`;

/** Eixo X das séries: o rótulo muda conforme o backend agregou por dia ou semana. */
export const bucketLabel = (bucket: string | null, unit: 'day' | 'week') => {
  if (!bucket) return '';
  const d = dayjs(bucket);
  if (!d.isValid()) return bucket;
  return unit === 'week' ? `sem. ${d.format('DD/MM')}` : d.format('DD/MM');
};

export const Card = ({ title, hint, right, children, sx }: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}) => (
  <Box sx={{ ...cardSx, ...sx }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: hint ? 1.5 : 1.25 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{hint}</Typography>}
      </Box>
      {right && <Box sx={{ ml: 'auto', flexShrink: 0 }}>{right}</Box>}
    </Box>
    {children}
  </Box>
);

export const Stat = ({ label, value, color, sub, hint }: {
  label: string;
  value: React.ReactNode;
  color?: string;
  sub?: React.ReactNode;
  hint?: string;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>
        {label}
      </Typography>
      {hint && (
        <Tooltip arrow title={hint}>
          <InfoOutlinedIcon sx={{ fontSize: 12.5, color: COLORS.faint }} />
        </Tooltip>
      )}
    </Box>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: color ?? COLORS.ink, lineHeight: 1.25 }}>{value}</Typography>
    {sub && <Typography component="div" sx={{ fontSize: 11.5, color: COLORS.muted }}>{sub}</Typography>}
  </Box>
);

/**
 * Barra horizontal com o valor DENTRO quando ela é larga e FORA, em cor escura,
 * quando é estreita (<22%). Sem isso, o texto branco cai sobre fundo claro e
 * fica ilegível — mesmo critério do Painel 360 (EngagementSection).
 */
export const MetricBar = ({ label, value, pctOfMax, color, textColor, labelWidth = 150 }: {
  label: React.ReactNode;
  value: string;
  pctOfMax: number;
  color: string;
  textColor?: string;
  labelWidth?: number;
}) => {
  const safe = Number.isFinite(pctOfMax) ? Math.max(0, Math.min(1, pctOfMax)) : 0;
  const wide = safe >= 0.22;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.9 }}>
      <Typography
        component="div"
        sx={{
          width: labelWidth, flexShrink: 0, fontSize: 12, color: COLORS.muted, fontWeight: 600,
          textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(1.5, safe * 100)}%` }}
          transition={{ duration: 0.9 }}
          style={{
            height: 26, borderRadius: 7, background: color, flexShrink: 0,
            display: 'flex', alignItems: 'center', paddingLeft: wide ? 10 : 0, minWidth: wide ? undefined : 6,
          }}
        >
          {wide && (
            <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: textColor ?? '#fff', whiteSpace: 'nowrap' }}>
              {value}
            </Typography>
          )}
        </motion.div>
        {!wide && (
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: COLORS.ink, whiteSpace: 'nowrap' }}>{value}</Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Legenda de recorte. Onde há corte, o número aparece — a tela antiga cortava
 * em slice(0,10) sem dizer e o operador achava que estava vendo tudo.
 */
export const ShowingCount = ({ shown, total, noun }: { shown: number; total: number; noun: string }) => (
  <Typography sx={{ fontSize: 11.5, color: shown < total ? COLORS.warning : COLORS.faint, fontWeight: shown < total ? 700 : 400 }}>
    {total === 0 ? `nenhum ${noun} no recorte` : `mostrando ${shown} de ${total} ${noun}${total === 1 ? '' : 's'}`}
  </Typography>
);

/** Texto que o backend manda e a UI é obrigada a exibir (disclaimer, note, reason). */
export const BackendNote = ({ children, tone = 'muted' }: {
  children: React.ReactNode;
  tone?: 'muted' | 'warning';
}) => (
  <Typography sx={{
    fontSize: 11.5, mt: 1, lineHeight: 1.55,
    color: tone === 'warning' ? COLORS.warning : COLORS.faint,
    fontWeight: tone === 'warning' ? 600 : 400,
  }}>
    {children}
  </Typography>
);

/**
 * Estado INDISPONÍVEL — terceiro estado, distinto de erro e de vazio.
 * Fundo âmbar, ícone de informação e o motivo que o backend mandou. Erro é
 * cinza com botão de retry; vazio é texto neutro; isto aqui diz "a fonte não
 * existe pra esta rede", que é outra conversa.
 */
export const UnavailableHint = ({ reason }: { reason?: string }) => (
  <Box sx={{
    display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2, py: 1.75, borderRadius: 3,
    bgcolor: 'rgba(245,158,11,0.08)', border: '1px dashed rgba(245,158,11,0.45)',
  }}>
    <InfoOutlinedIcon sx={{ fontSize: 18, color: COLORS.warning, mt: '1px' }} />
    <Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>Indisponível para esta rede</Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.25 }}>
        {reason ?? 'A fonte desta seção não tem dados para esta rede.'}
      </Typography>
    </Box>
  </Box>
);

/** Chip on/off, usado nas alavancas de ticket e na matriz de módulos. */
export const OnOffChip = ({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) => (
  <Box component="span" sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: 999, px: 1, py: 0.25,
    fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
    color: on ? COLORS.success : COLORS.faint,
    bgcolor: on ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.16)',
    border: `1px solid ${on ? 'rgba(34,197,94,0.35)' : 'rgba(148,163,184,0.35)'}`,
  }}>
    {on ? '●' : '○'} {on ? onLabel : offLabel}
  </Box>
);

/** Grid de estatísticas que se adapta — usado no cabeçalho de várias seções. */
export const StatGrid = ({ children, min = 150 }: { children: React.ReactNode; min?: number }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, rowGap: 2 }}>
    {children}
  </Box>
);
